import type { Me } from './api';

// ─────────────────────────────────────────────────────────────────────────────
// orgAccess — the SINGLE reusable frontend org-scope check (org-admin
// de-gate, auth/RBAC work). Answers exactly two questions:
//   "is the current user an ORG_ADMIN or ORG_OWNER of org X?"
//   "...of the org that owns comp/clan Y?"
//
// Every org-admin de-gate surface (Sidebar's Run It group, TournamentSetup's
// cockpit gate + org/comp selectors, ClanManagement's gate + comp selector,
// MatchCardLive's score-inspect/confirm/edit gates) calls through here — no
// ad-hoc `role === 'ORG_ADMIN'` checks anywhere else. Reads the user's org
// memberships already loaded on the frontend (Me.orgMemberships, populated at
// bootstrap by GET /me and cached under the ['me'] react-query key every
// surface already shares — see workspace.ts) — never invents a new fetch.
//
// Mirrors the backend's canManageOrgCapability (portalApi/index.ts:1666-1676)
// EXACTLY: platform founder/admin short-circuit true, THEN fall through to
// the caller's own org-membership row. Helper does NOT get the short-circuit
// bypass — same as the backend (canManageOrgCapability checks
// `platformRole === 'founder' || platformRole === 'admin'` only).
//
// CLIENT GATING IS UX-ONLY (same posture as pages/gate/gateIdentity.ts) —
// every destination this unlocks still enforces its own authz server-side
// (canManageOrgCapability / isOrgCompManager / isCompStructureManager). This
// module only decides what to *show*; no secrets gate on it.
//
// orgId shape convention this codebase already established (see
// useCompChecklist.ts / TournamentSetup.tsx's own bareOrgId dance, and the
// org-scoped-visibility-boundary memory note): a comp/clan's own `orgId`
// field is stored BARE; `me.orgMemberships[].orgId` is stored ORG#-PREFIXED
// (it's the USER#<id>/ORG#<id> reverse-index row's sk, see portalApi's
// listUserOrganizations). Every comparison below normalizes both sides so
// callers never have to think about which shape they're holding.
// ─────────────────────────────────────────────────────────────────────────────

const ORG_MANAGER_ROLES = ['ORG_OWNER', 'ORG_ADMIN'] as const;

function bareOrgId(orgId: string): string {
  return orgId.replace(/^ORG#/, '');
}

/** Platform-tier short-circuit — founder/admin ONLY (mirrors
 *  canManageOrgCapability; helper does NOT get this bypass, same as the
 *  backend). */
export function isPlatformOrgManager(me: Me | null | undefined): boolean {
  return me?.role === 'founder' || me?.role === 'admin';
}

/** Pure membership check — is `me` an ORG_ADMIN/ORG_OWNER of `orgId` (bare or
 *  ORG#-prefixed, either side)? NO platform short-circuit. For call sites
 *  that already compute their own view-as-aware admin flag and OR it in
 *  themselves — mirrors MatchCardLive.tsx's canOperateDigestion precedent,
 *  where the founder leg goes through effectiveRole (so a founder previewing
 *  as a lower role correctly loses it) but the org-membership leg never does. */
export function hasOrgManagerMembership(me: Me | null | undefined, orgId: string | null | undefined): boolean {
  if (!orgId) return false;
  const target = bareOrgId(orgId);
  return (me?.orgMemberships ?? []).some(
    m => bareOrgId(m.orgId) === target && (ORG_MANAGER_ROLES as readonly string[]).includes(m.role),
  );
}

/** Full check, WITH the platform founder/admin short-circuit — for surfaces
 *  with no separate view-as-aware admin flag of their own (Sidebar,
 *  TournamentSetup, ClanManagement). */
export function isOrgManagerOf(me: Me | null | undefined, orgId: string | null | undefined): boolean {
  return isPlatformOrgManager(me) || hasOrgManagerMembership(me, orgId);
}

/** Is `me` an org manager of ANY org they belong to? Drives "any org" nav
 *  visibility (Sidebar's Run It group) — the pages themselves scope down to
 *  the SPECIFIC org via isOrgManagerOf/isOrgManagerOfEntity above/below. */
export function isOrgManagerOfAny(me: Me | null | undefined): boolean {
  if (isPlatformOrgManager(me)) return true;
  return (me?.orgMemberships ?? []).some(m => (ORG_MANAGER_ROLES as readonly string[]).includes(m.role));
}

/** Convenience over an entity carrying its own `orgId` (a Competition/Clan).
 *  An entity with no orgId at all can never be matched by an org manager —
 *  only founder/admin may act on an org-less (legacy) row. */
export function isOrgManagerOfEntity(
  me: Me | null | undefined,
  entity: { orgId?: string | null } | null | undefined,
): boolean {
  return isOrgManagerOf(me, entity?.orgId ?? null);
}

/** Pure-membership variant of isOrgManagerOfEntity — see
 *  hasOrgManagerMembership's no-short-circuit reasoning. */
export function hasOrgManagerMembershipOfEntity(
  me: Me | null | undefined,
  entity: { orgId?: string | null } | null | undefined,
): boolean {
  return hasOrgManagerMembership(me, entity?.orgId ?? null);
}

/** The bare orgIds `me` manages (ORG_ADMIN/ORG_OWNER) — for scoping org/comp
 *  selectors down to "only mine". Founders/platform-admins get the 'all'
 *  sentinel (no FE filtering needed — GET /orgs already returns everything
 *  for them server-side). */
export function managedOrgIds(me: Me | null | undefined): string[] | 'all' {
  if (isPlatformOrgManager(me)) return 'all';
  return (me?.orgMemberships ?? [])
    .filter(m => (ORG_MANAGER_ROLES as readonly string[]).includes(m.role))
    .map(m => bareOrgId(m.orgId));
}

// ── Owner-only checks (2026-07-17) ──────────────────────────────────────────
// A handful of surfaces (competition activate/end/advance-stage/create-
// divisions) are OWNER-ONLY — Lisa's explicit call that ORG_ADMIN does NOT
// get this power, unlike every other org-manager widen in this codebase.
// Mirrors the backend's canActivateCompetitionOrgOwner (portalApi/index.ts)
// EXACTLY: no platform short-circuit baked in here either — callers OR in
// their own canWrite(role)/founder-admin check themselves (see
// ActivateCompetitionButton), same posture as hasOrgManagerMembership above.

/** Pure membership check — is `me` SPECIFICALLY an ORG_OWNER (not ORG_ADMIN)
 *  of `orgId`? No platform short-circuit. */
export function hasOrgOwnerMembership(me: Me | null | undefined, orgId: string | null | undefined): boolean {
  if (!orgId) return false;
  const target = bareOrgId(orgId);
  return (me?.orgMemberships ?? []).some(m => bareOrgId(m.orgId) === target && m.role === 'ORG_OWNER');
}

/** Convenience over an entity carrying its own `orgId` (a Competition). */
export function hasOrgOwnerMembershipOfEntity(
  me: Me | null | undefined,
  entity: { orgId?: string | null } | null | undefined,
): boolean {
  return hasOrgOwnerMembership(me, entity?.orgId ?? null);
}
